import { computed, reactive } from 'vue';
import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';
import { STEP_STATUS, STEP_TYPE, StepInfo } from '@/components/stepper/stepper.types';
import { SELECTED_NETWORK_NAME } from '@/helpers/networkParams.helper';
import { useWallet } from '@/store/modules/wallet/useWallet';
import { usePortfoliosMilkomedaWSCBridge } from '@/store/modules/portfolios/usePortfoliosMilkomedaWSCBridge';
import { usePortfoliosMilkomedaWSCUnwrapBridge } from '@/store/modules/portfolios/usePortfoliosMilkomedaWSCUnwrapBridge';
import { useEasyModeWithdraw } from './useEasyModeWithdraw';
import { useEasyModeTransactions } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeTransactions';

interface IWithdrawStepsState {
  steps: StepInfo[];
  currentStep: StepInfo | null;
  isShown: boolean;
  inProgress: boolean;
}

export const useEasyModeWithdrawSteps = defineStore('easyModeWithdrawSteps', () => {
  const { t } = useI18n();
  const { walletState } = useWallet();
  const { updatePortfolios } = useEasyModeTransactions();
  const { withdrawForm, setAllowance, doWithdraw } = useEasyModeWithdraw();
  const { milkomedaWSCBridgeState, doBridgeFromCardano } = usePortfoliosMilkomedaWSCBridge();
  const { milkomedaWSCUnwrapBridgeState, doApproveForBridgeFromMilkomeda, doBridgeFromMilkomeda } =
    usePortfoliosMilkomedaWSCUnwrapBridge();

  const walletName = computed(() => {
    return walletState.wallets[SELECTED_NETWORK_NAME].connector.name;
  });

  const doStepAction = {
    [STEP_TYPE.BRIDGE]: async () => {
      await doBridgeFromCardano();
    },
    [STEP_TYPE.APPROVE_LP]: async () => {
      await setAllowance();
    },
    [STEP_TYPE.SIGN]: async () => {
      await doWithdraw();
    },
    [STEP_TYPE.REVERSE_APPROVE]: async () => {
      await doApproveForBridgeFromMilkomeda();
    },
    [STEP_TYPE.REVERSE_BRIDGE]: async () => {
      await doBridgeFromMilkomeda();
      await updatePortfolios();
    },
  };

  let nextStep = {};

  const withdrawSteps = reactive<IWithdrawStepsState>({
    steps: [],
    currentStep: null,
    isShown: false,
    inProgress: false,
  });

  function $reset() {
    nextStep = {};
    withdrawSteps.steps = [];
    withdrawSteps.currentStep = null;
    withdrawSteps.isShown = false;
    withdrawSteps.inProgress = false;
  }

  // BRIDGE
  function addBridgeStep(
    currentStep: StepInfo | null,
    prevStep: STEP_TYPE | null,
  ): {
    prevStep: STEP_TYPE | null;
    currentStep: StepInfo | null;
  } {
    if (milkomedaWSCBridgeState.needBridge) {
      const step = {
        name: t('portfolio.steps.bridge'),
        type: STEP_TYPE.BRIDGE,
        status: currentStep ? STEP_STATUS.NULL : STEP_STATUS.ACTIVE,
      };
      withdrawSteps.steps.push(step);
      // set current step
      if (!currentStep) {
        currentStep = step;
      }
      return {
        prevStep: STEP_TYPE.BRIDGE,
        currentStep,
      };
    }

    return {
      prevStep,
      currentStep,
    };
  }

  // APPROVE
  function addApproveLPStep(
    currentStep: StepInfo | null,
    prevStep: STEP_TYPE | null,
  ): {
    prevStep: STEP_TYPE | null;
    currentStep: StepInfo | null;
  } {
    if (withdrawForm.needApproveLP) {
      const step = {
        name: t('portfolio.steps.approveLP'),
        type: STEP_TYPE.APPROVE_LP,
        status: currentStep ? STEP_STATUS.NULL : STEP_STATUS.ACTIVE,
      };
      withdrawSteps.steps.push(step);
      // set current step
      if (!currentStep) {
        currentStep = step;
      }
      // next step
      if (prevStep) {
        nextStep[prevStep] = step;
      }
      return {
        prevStep: STEP_TYPE.APPROVE_LP,
        currentStep,
      };
    }

    return {
      prevStep,
      currentStep,
    };
  }

  // SIGN
  function addSignStep(
    currentStep: StepInfo | null,
    prevStep: STEP_TYPE | null,
  ): {
    prevStep: STEP_TYPE | null;
    currentStep: StepInfo | null;
  } {
    const step = {
      name: t('portfolio.steps.sign.withdraw'),
      type: STEP_TYPE.SIGN,
      status: currentStep ? STEP_STATUS.NULL : STEP_STATUS.ACTIVE,
    };
    withdrawSteps.steps.push(step);
    // set current step
    if (!currentStep) {
      currentStep = step;
    }
    // next step
    if (prevStep) {
      nextStep[prevStep] = step;
    }

    return {
      prevStep: STEP_TYPE.SIGN,
      currentStep,
    };
  }

  // REVERSE APPROVE
  function addReverseApproveStep(
    currentStep: StepInfo | null,
    prevStep: STEP_TYPE | null,
  ): {
    prevStep: STEP_TYPE | null;
    currentStep: StepInfo | null;
  } {
    if (
      milkomedaWSCUnwrapBridgeState.needRevertBridge &&
      milkomedaWSCUnwrapBridgeState.bridge.length
    ) {
      const step = {
        name: t('portfolio.steps.reverseApprove'),
        type: STEP_TYPE.REVERSE_APPROVE,
        status: currentStep ? STEP_STATUS.NULL : STEP_STATUS.ACTIVE,
      };
      withdrawSteps.steps.push(step);
      // set current step
      if (!currentStep) {
        currentStep = step;
      }
      // next step
      if (prevStep) {
        nextStep[prevStep] = step;
      }
      return {
        prevStep: STEP_TYPE.REVERSE_APPROVE,
        currentStep,
      };
    }

    return {
      prevStep,
      currentStep,
    };
  }

  // REVERSE BRIDGE
  function addReverseBridgeStep(
    currentStep: StepInfo | null,
    prevStep: STEP_TYPE | null,
  ): {
    prevStep: STEP_TYPE | null;
    currentStep: StepInfo | null;
  } {
    if (milkomedaWSCUnwrapBridgeState.needRevertBridge) {
      const step = {
        name: t('portfolio.steps.reverseBridge', { wallet: walletName.value }),
        type: STEP_TYPE.REVERSE_BRIDGE,
        status: currentStep ? STEP_STATUS.NULL : STEP_STATUS.ACTIVE,
      };
      withdrawSteps.steps.push(step);
      // set current step
      if (!currentStep) {
        currentStep = step;
      }
      // next step
      if (prevStep) {
        nextStep[prevStep] = step;
      }
      return {
        prevStep: STEP_TYPE.REVERSE_BRIDGE,
        currentStep,
      };
    }

    return {
      prevStep,
      currentStep,
    };
  }

  function prepareSteps(): void {
    let step: StepInfo | null = null;
    let prevStep: STEP_TYPE | null = null;

    let state = addBridgeStep(step, prevStep);
    step = state.currentStep;
    prevStep = state.prevStep;

    state = addApproveLPStep(step, prevStep);
    step = state.currentStep;
    prevStep = state.prevStep;

    state = addSignStep(step, prevStep);
    step = state.currentStep;
    prevStep = state.prevStep;

    state = addReverseApproveStep(step, prevStep);
    step = state.currentStep;
    prevStep = state.prevStep;

    state = addReverseBridgeStep(step, prevStep);
    step = state.currentStep;
    prevStep = state.prevStep;

    // next step
    if (prevStep) {
      nextStep[prevStep] = null;
    }

    withdrawSteps.currentStep = step;
  }

  async function doStep(step: StepInfo) {
    step.status = STEP_STATUS.PROGRESS;
    await doStepAction[step.type]();
    step.status = STEP_STATUS.SUCCESS;
    withdrawSteps.currentStep = nextStep[step.type];
  }

  async function runSteps() {
    if (!withdrawSteps.currentStep) return;
    withdrawSteps.inProgress = true;

    try {
      while (withdrawSteps.currentStep) {
        await doStep(withdrawSteps.currentStep);
      }
    } catch (err) {
      withdrawSteps.currentStep.status = STEP_STATUS.ERROR;
      throw Error(err);
    } finally {
      withdrawSteps.inProgress = false;
    }
  }

  return {
    withdrawSteps,
    prepareSteps,
    runSteps,
    $reset,
  };
});
